import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { useTrip } from './hooks/useTrip';
import { DesignProvider } from './designs';
import { lookupShareCode } from './services/sharing';
import { signInAnon } from './services/auth';
import { trackShareLinkOpened } from './config/analytics';
import { Trip, TripMember } from './types/trip';
import { getMember, addTripMember, getTrip, getClaimedMember, claimCrewMember, addNewCrewMemberWithClaim } from './services/trips';
import CrewClaimScreen from './screens/trip/CrewClaimScreen';
import LoginScreen from './screens/auth/LoginScreen';
import SignUpScreen from './screens/auth/SignUpScreen';
import ForgotPasswordScreen from './screens/auth/ForgotPasswordScreen';
import DashboardScreen from './screens/dashboard/DashboardScreen';
import OnboardingStep1Dates from './screens/onboarding/OnboardingStep1Dates';
import OnboardingStep2Crew from './screens/onboarding/OnboardingStep2Crew';
import OnboardingStep3Lodging from './screens/onboarding/OnboardingStep3Lodging';
import TripScreen from './screens/trip/TripScreen';
import PaywallScreen from './screens/paywall/PaywallScreen';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#130E1C',
        fontFamily: "'Bodoni Moda', serif",
        color: '#EDE4D8',
        fontSize: '24px',
        fontWeight: 700,
      }}>
        KREWE
      </div>
    );
  }

  if (!user || user.isAnonymous) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/**
 * Wraps trip routes with the trip's design.
 * Resolves designId from the trip's Firebase meta.
 */
function TripDesignWrapper({ children }: { children: React.ReactNode }) {
  const { tripId } = useParams<{ tripId: string }>();
  const { data: trip } = useTrip(tripId || '');
  const designId = trip?.meta?.designId || 'parlor-screen';

  return (
    <DesignProvider designId={designId}>
      {children}
    </DesignProvider>
  );
}

/**
 * Resolves /t/:shareCode → anonymous auth → crew identity claim → TripScreen.
 *
 * Flow:
 * 1. Look up shareCode → tripId
 * 2. Anonymous auth (if not already signed in)
 * 3. Check if this UID already claimed a crew member (via claimedBy field)
 *    - If yes → skip interstitial, load trip directly
 *    - If no → show CrewClaimScreen interstitial
 * 4. After claim, proceed to trip view
 */
function ShareCodeResolver() {
  const { shareCode } = useParams<{ shareCode: string }>();
  const { user, isLoading: authLoading } = useAuth();
  const [tripId, setTripId] = useState<string | null>(null);
  const [lookupDone, setLookupDone] = useState(false);
  const [claimCheckDone, setClaimCheckDone] = useState(false);
  const [hasClaim, setHasClaim] = useState(false);
  const [showInterstitial, setShowInterstitial] = useState(false);
  const [tripData, setTripData] = useState<Trip | null>(null);
  const [error, setError] = useState('');

  // Step 1: shareCode → tripId
  useEffect(() => {
    if (!shareCode) return;
    console.log('[ShareLink] Step 1: Looking up share code:', shareCode);
    lookupShareCode(shareCode).then((id) => {
      console.log('[ShareLink] Step 1 result:', id ? `tripId=${id}` : 'NOT FOUND');
      if (id) setTripId(id);
      else setError('Trip not found');
      setLookupDone(true);
    }).catch((err) => { console.error('[ShareLink] Step 1 FAILED:', err); setError('Trip not found'); setLookupDone(true); });
  }, [shareCode]);

  // Step 2: Anonymous auth if not signed in
  useEffect(() => {
    if (authLoading || !lookupDone || !tripId) return;
    if (!user) {
      console.log('[ShareLink] Step 2: No user, calling signInAnon...');
      signInAnon().then(() => {
        console.log('[ShareLink] Step 2: Anonymous auth succeeded');
      }).catch((err) => {
        console.error('[ShareLink] Step 2 FAILED: Anonymous auth error:', err?.code, err?.message);
        setError('Unable to access trip');
      });
    } else {
      console.log('[ShareLink] Step 2: User already authenticated:', user.uid, 'anonymous:', user.isAnonymous);
    }
  }, [authLoading, lookupDone, tripId, user]);

  // Step 3: Check if user already claimed a crew member
  useEffect(() => {
    if (!user || !tripId) return;
    console.log('[ShareLink] Step 3: Checking claims. uid:', user.uid, 'anonymous:', user.isAnonymous);
    trackShareLinkOpened(tripId, user.isAnonymous);

    // Non-anonymous users (planners who happen to open the share link) skip the claim
    if (!user.isAnonymous) {
      console.log('[ShareLink] Step 3a: Non-anonymous user, checking membership...');
      getMember(tripId, user.uid).then((existing) => {
        if (existing) {
          console.log('[ShareLink] Step 3a: Already a member, role:', existing.role);
          setHasClaim(true);
          setClaimCheckDone(true);
          return;
        }
        console.log('[ShareLink] Step 3a: Not a member, adding...');
        const memberData: TripMember = {
          uid: user.uid,
          displayName: user.displayName || 'Guest',
          initials: user.displayName?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?',
          role: 'crew',
          isAnonymous: false,
          joinedAt: Date.now(),
          claimedBy: user.uid,
        };
        return addTripMember(tripId, user.uid, memberData).then(() => {
          console.log('[ShareLink] Step 3a: Added as crew member');
          setHasClaim(true);
          setClaimCheckDone(true);
        });
      }).catch((err) => { console.error('[ShareLink] Step 3a FAILED:', err?.code, err?.message, err); setError('Unable to join trip'); });
      return;
    }

    // Anonymous user: check if they already claimed someone
    console.log('[ShareLink] Step 3b: Anonymous user, checking claimIndex...');
    getClaimedMember(tripId, user.uid).then((claimed) => {
      if (claimed) {
        console.log('[ShareLink] Step 3b: Already claimed member:', claimed);
        setHasClaim(true);
        setClaimCheckDone(true);
      } else {
        console.log('[ShareLink] Step 3b: No claim, showing interstitial...');
        getTrip(tripId).then((trip) => {
          console.log('[ShareLink] Step 3b: Trip data loaded for interstitial');
          setTripData(trip);
          setShowInterstitial(true);
          setClaimCheckDone(true);
        }).catch((err) => { console.error('[ShareLink] Step 3b getTrip FAILED:', err?.code, err?.message, err); setError('Unable to access trip'); });
      }
    }).catch((err) => { console.error('[ShareLink] Step 3b FAILED:', err?.code, err?.message, err); setError('Unable to access trip'); });
  }, [user, tripId]);

  // Handle claiming an existing crew member name
  const handleClaimExisting = async (crewMemberUid: string) => {
    if (!tripId || !user) return;
    await claimCrewMember(tripId, crewMemberUid, user.uid);
    setHasClaim(true);
    setShowInterstitial(false);
  };

  // Handle entering a new name
  const handleClaimNew = async (displayName: string) => {
    if (!tripId || !user) return;
    await addNewCrewMemberWithClaim(tripId, user.uid, displayName);
    setHasClaim(true);
    setShowInterstitial(false);
  };

  const loadingSpinner = (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#130E1C' }}>
      <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: '7px', fontWeight: 800, letterSpacing: '9px', textTransform: 'uppercase', color: 'rgba(200,146,58,0.18)' }}>KREWE</span>
    </div>
  );

  // Loading state
  if (!lookupDone || authLoading || (!user && !error)) return loadingSpinner;
  if (user && !claimCheckDone && !error) return loadingSpinner;

  if (error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#130E1C' }}>
        <p style={{ fontFamily: "'Raleway', sans-serif", color: '#938EA4' }}>{error}</p>
      </div>
    );
  }

  // Show interstitial if user hasn't claimed yet
  if (showInterstitial && !hasClaim && tripData) {
    return (
      <ShareCodeTripDesignWrapper tripId={tripId!}>
        <CrewClaimScreen
          meta={tripData.meta}
          members={tripData.members || {}}
          onClaimExisting={handleClaimExisting}
          onClaimNew={handleClaimNew}
        />
      </ShareCodeTripDesignWrapper>
    );
  }

  // Claimed — render TripScreen
  if (hasClaim) {
    return (
      <ShareCodeTripDesignWrapper tripId={tripId!}>
        <TripScreen tripIdOverride={tripId!} />
      </ShareCodeTripDesignWrapper>
    );
  }

  return loadingSpinner;
}

/** Like TripDesignWrapper but takes tripId as a prop instead of from URL params. */
function ShareCodeTripDesignWrapper({ tripId, children }: { tripId: string; children: React.ReactNode }) {
  const { data: trip } = useTrip(tripId);
  const designId = trip?.meta?.designId || 'parlor-screen';
  return <DesignProvider designId={designId}>{children}</DesignProvider>;
}

function App() {
  return (
    <BrowserRouter>
      <div style={{
        backgroundColor: '#130E1C',
        minHeight: '100vh',
        fontFamily: "'Raleway', sans-serif",
        color: '#EDE4D8',
      }}>
        <Routes>
          {/* Public routes — planner chrome design */}
          <Route path="/login" element={<DesignProvider designId="parlor-screen"><LoginScreen /></DesignProvider>} />
          <Route path="/signup" element={<DesignProvider designId="parlor-screen"><SignUpScreen /></DesignProvider>} />
          <Route path="/forgot-password" element={<DesignProvider designId="parlor-screen"><ForgotPasswordScreen /></DesignProvider>} />
          <Route path="/t/:shareCode" element={<ShareCodeResolver />} />

          {/* Protected routes — planner chrome design */}
          <Route path="/" element={<ProtectedRoute><DesignProvider designId="parlor-screen"><DashboardScreen /></DesignProvider></ProtectedRoute>} />
          <Route path="/new-trip" element={<ProtectedRoute><DesignProvider designId="parlor-screen"><OnboardingStep1Dates /></DesignProvider></ProtectedRoute>} />
          <Route path="/new-trip/crew" element={<ProtectedRoute><DesignProvider designId="parlor-screen"><OnboardingStep2Crew /></DesignProvider></ProtectedRoute>} />
          <Route path="/new-trip/lodging" element={<ProtectedRoute><DesignProvider designId="parlor-screen"><OnboardingStep3Lodging /></DesignProvider></ProtectedRoute>} />

          {/* Trip routes — design from trip meta */}
          <Route path="/trip/:tripId" element={<ProtectedRoute><TripDesignWrapper><TripScreen /></TripDesignWrapper></ProtectedRoute>} />
          <Route path="/trip/:tripId/paywall" element={<ProtectedRoute><TripDesignWrapper><PaywallScreen /></TripDesignWrapper></ProtectedRoute>} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;
